import React, { useCallback, useMemo, useState } from 'react';

import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';

import { trackEvent } from 'src/lib/analytics';
import jira from 'src/lib/api/jira';
import pagerduty from 'src/lib/api/pagerduty';
import { listAllIncidentWorkflowTriggers } from 'src/lib/api/pagerdutyIncidentWorkflowHelpers';
import { NoAccessToIncidentWorkflowsError } from 'src/lib/error';
import { getQueryParams } from 'src/lib/history';

import ErrorMessage from 'src/views/components/ErrorMessage';
import Field from 'src/views/components/Form/Field';
import NoAccessToIncidentWorkflowsMessage from 'src/views/components/messages/NoAccessToIncidentWorkflowsMessage';
import NoIncidentWorkflowsMessage from 'src/views/components/messages/NoIncidentWorkflowsMessage';
import ModalContainer from 'src/views/components/ModalContainer';
import { PdIncidentWorkflowTriggerSelect } from 'src/views/components/select';

const analyticsEventName = 'sidebar_incidentresponse_run_workflow';
const ModalRunIncidentWorkflow = () => {
  const [value, setValue] = useState(null);
  const [{ submitError, isSubmitting }, setSubmitStatus] = useState({});
  const [errorComponent, setErrorComponent] = useState(null);
  const incidentId = getQueryParams().incidentId;

  const closeModal = () => {
    trackEvent(analyticsEventName, { label: 'cancel' });
    jira.closeCurrentModal();
  };

  const onSelect = useCallback((event) => {
    // clear error if any
    setSubmitStatus({});
    setValue(event.target.value || null);
  }, []);

  const onSubmit = useCallback(() => {
    setSubmitStatus({ isSubmitting: true });
    trackEvent(analyticsEventName, { label: 'run_workflow' });
    pagerduty.triggerIncidentWorkflow(value.id, getQueryParams().incidentId)
      .then(() => { jira.closeCurrentModal(); })
      .catch(submitError => setSubmitStatus({ submitError }));
  }, [value]);

  const optionSources = useMemo(async () => {
    try {
      const triggers = await listAllIncidentWorkflowTriggers({
        incident_id: incidentId,
        trigger_type: 'manual',
        sort_by: 'workflow_name'
      });

      return triggers.map(t => ({
        label: t.workflow_name,
        id: t.id,
        workflow_id: t.workflow_id,
        description: t.workflow.description
      }));
    } catch (e) {
      if (e instanceof NoAccessToIncidentWorkflowsError) {
        setErrorComponent(<NoAccessToIncidentWorkflowsMessage/>);
        return [];
      } else {
        setErrorComponent(true);
        throw e;
      }
    }
  }, [incidentId]);

  let noOptionsMessageProp = {};
  if (errorComponent === null) {
    noOptionsMessageProp = { noOptionsMessage: () => {
      return <NoIncidentWorkflowsMessage utmContent = "run_workflow_dialog_no_options" />;
    } };
  }

  return (
    <ModalContainer title="Run Workflow">
      <Field label="Select Workflow" isRequired>
        <PdIncidentWorkflowTriggerSelect
          {...noOptionsMessageProp}
          optionSources={optionSources}
          errorComponent={errorComponent}
          className="workflow-select"
          onChange={onSelect}
        />
      </Field>
      <div style={{ marginTop: 20, minHeight: 280 }}>
        {submitError && <ErrorMessage error={['Can not run Workflow', submitError]}/>}
      </div>
      <div style={{ paddingTop: 24, paddingBottom: 24, display: 'flex', justifyContent: 'flex-end' }}>
        <ButtonGroup>
          <Button onClick={closeModal} isDisabled={isSubmitting}>Cancel</Button>
          <LoadingButton appearance="primary" isLoading={isSubmitting} onClick={onSubmit} isDisabled={!value}>Run</LoadingButton>
        </ButtonGroup>
      </div>
    </ModalContainer>
  );
};

export default ModalRunIncidentWorkflow;
